import ContentLayout from '@cloudscape-design/components/content-layout';
import FilterModal from 'src/components/filter/FilterModel';
import {
  Box,
  Button,
  Header,
  Pagination,
  PropertyFilterProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination, { AppPaginationRef } from 'src/components/AppPagination';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import { Persona } from 'src/contstant/Persona';
import routes from 'src/contstant/routePaths';
import {
  defaultVisibleColumns,
  exportTemplatesConfig,
  getFilterTemplatesFilterProperties,
  getItemValue,
  getSearchableFields,
  getTemplatesTableDefinition,
  initialFilters,
} from './templatesConfig';
import { ExportButton } from 'src/components/ExportButton';
import { useTemplates } from 'src/store/templates/useTemplates';
import useUserProfile from 'src/hooks/useUserProfile';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import TablePreferences, { ColumnDefinition } from 'src/components/TablePreferences';
import { PAGE_SIZE_OPTIONS } from 'src/contstant/commonLabels';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { countActiveFilters } from 'src/utils/filterPersistence';
import { usePropertyFilterPersistence } from 'src/hooks/usePropertyFilterPersistence';
import { useActivePropertyFilterCount } from 'src/hooks/useActivePropertyFilterCount';
import { getPageFilterKey } from 'src/contstant/storageKeys';

const TemplatesPage = () => {
  const navigate = useNavigate();

  const { getUserProgramId, getUserId, getPersona, getUserBusinessUnitId, getDivisions } =
    useUserProfile();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): any => {
    return {
      tokens: [],
      operation: 'and' as const,
      searchText: '',
    };
  }, []);

  // Use filter persistence hook
  const { filters: queryFilters, setFilters: setQueryFilters } = useFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'templates'),
    defaultFilters: getDefaultFilters(),
    excludeFields: ['businessUnitId'],
    businessUnitId,
  });

  // Count active filters
  const activeFilterCount = useActivePropertyFilterCount(queryFilters);

  // Table preferences state
  const [tablePrefs, setTablePrefs] = useState({
    visibleColumns: getTemplatesTableDefinition().filter((col) =>
      defaultVisibleColumns.includes(col.id),
    ),
    paginatedItems: [],
    currentPage: 1,
    pageSize: PaginationConfig.TEMPLATES_MAX_RESULT,
  });

  // Handler for table preferences changes
  const handlePreferencesChange = (data: {
    visibleColumns: any[];
    paginatedItems: any[];
    currentPage: number;
    pageSize: number;
  }) => {
    // Use type assertion to match the expected state type
    setTablePrefs((prev) => ({
      ...prev,
      visibleColumns: data.visibleColumns,
      currentPage: data.currentPage,
      pageSize: data.pageSize as PaginationConfig,
      // Keep the paginatedItems type as is from the previous state
      // but update with new values
      paginatedItems: data.paginatedItems as typeof prev.paginatedItems,
    }));
  };

  const {
    templatesList,
    fetchTemplatesApiStatus,
    loadTemplatesList,
    clearStatusMessages,
  } = useTemplates();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const { checkUserPermissions, userPermission } = useUsers();

  const {
    searchText,
    setSearchText: setSearchTextInternal,
    query,
    setQuery,
    filteredItems,
    filterOptions,
    setFilterOptions,
  } = useGenericFilter<AuditTemplate>(
    templatesList,
    getItemValue,
    getSearchableFields,
    initialFilters,
    undefined, // nestedArrayConfig
    queryFilters.searchText || '', // Use persisted search text from queryFilters
  );

  // Temporary query state for filter modal (only applied on "Apply" click)
  const [tempQuery, setTempQuery] = useState(query);

  // Use custom hook for PropertyFilter persistence (search text + query tokens sync)
  const setSearchText = usePropertyFilterPersistence(
    setSearchTextInternal,
    setQueryFilters,
    queryFilters,
    setQuery,
  );

  // Local pagination
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: tablePrefs.pageSize,
    });

  const isEditTemplate = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.TEMPLATE.EDIT_TEMPLATE,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions, getPersona()]);

  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: businessUnitId,
      permissions: [permissionAccessConfig.TEMPLATE.EDIT_TEMPLATE],
      divisions: getDivisions(),
    });
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const loadData = () => {
    loadTemplatesList({
      program: getUserProgramId(),
      isActive: undefined,
      businessUnitId: getUserBusinessUnitId(),
    });
  };
  const tableHeader = (
    <Header
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          {/* Refresh data button */}
          <Button
            data-testid="refresh-button"
            iconName="refresh"
            onClick={() => loadData()}
          />
          <ExportButton
            data={filteredItems || []}
            config={exportTemplatesConfig}
            onError={(error) => {
              alert(error);
            }}
          />

          {isEditTemplate() && (
            <Button
              variant="normal"
              data-testid="create-template-button"
              onClick={() => {
                openTemplateDetailsPage();
              }}
            >
              Create Template
            </Button>
          )}
        </SpaceBetween>
      }
    >
      Templates
    </Header>
  );

  function openTemplateDetailsPage(data?: AuditTemplate) {
    navigate(routes.TEMPLATE_DETAILS.path, { state: { data: data } });
  }
  return (
    <ContentLayout>
      <ApiStatusFlashBar
        apiStatus={[fetchTemplatesApiStatus]}
        clearMessages={() => {
          clearStatusMessages();
        }}
      />
      <FilterModal
        visible={showFilterModal}
        onDismiss={() => {
          setTempQuery(query); // Reset temp query to current query
          setShowFilterModal(false);
        }}
        headerText="Filter Templates"
        query={tempQuery}
        onChange={({ detail }: { detail: PropertyFilterProps.Query }) => {
          setTempQuery(detail as any);
        }}
        loading={false}
        filteringProperties={getFilterTemplatesFilterProperties()}
        filteringOptions={filterOptions}
        filteringPlaceholder="Filter Templates"
        countText={`${filteredItems?.length} matches`}
        onApply={() => {
          setQuery(tempQuery); // Apply temp query to actual query
          setQueryFilters({ ...tempQuery, searchText: queryFilters.searchText });
          setShowFilterModal(false);
        }}
      />
      <Box margin={{ top: 's' }}>
        <Table
          data-testid="templates-table"
          columnDefinitions={
            tablePrefs.visibleColumns.length > 0
              ? tablePrefs.visibleColumns
              : getTemplatesTableDefinition()
          }
          items={paginatedItems}
          onRowClick={({ detail }) => {
            openTemplateDetailsPage(detail.item);
          }}
          loading={fetchTemplatesApiStatus.isLoading}
          loadingText="Loading Templates..."
          header={tableHeader}
          filter={
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchText}
                filteringPlaceholder="Search Templates"
                filteringAriaLabel="Search Templates"
                onChange={({ detail }) => {
                  setSearchText(detail.filteringText);
                }}
              />
              <Button
                data-testid="filter-button"
                iconName="filter"
                onClick={() => {
                  setTempQuery(query); // Initialize temp query with current query
                  setShowFilterModal(true);
                }}
              >
                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
              </Button>
            </SpaceBetween>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          preferences={
            <TablePreferences
              tableId="templates-table"
              defaultVisibleColumns={defaultVisibleColumns}
              defaultPageSize={PaginationConfig.TEMPLATES_MAX_RESULT}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              allColumnDefinitions={getTemplatesTableDefinition()}
              allItems={filteredItems || []}
              onPreferencesChange={handlePreferencesChange}
            />
          }
          stickyHeader
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Templates.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>
    </ContentLayout>
  );
};

export default TemplatesPage;
